import { useState } from 'react'
import { motion } from 'framer-motion'
import { Users, Clock, Smartphone, Trophy, Monitor, Building2, MapPin, ArrowRight } from 'lucide-react'


const FRANCHISE_URL = 'https://gzzh.feishu.cn/share/base/form/shrcnZ7Qv6q2GISsEyTjGASTzAb'

const features = [
  {
    icon: Building2,
    title: '社区化连锁门店',
    desc: '健身馆建立在居民社区，到馆的距离不超过5分钟',
    color: '#ec4899',
  },
  {
    icon: Clock,
    title: '24小时营业',
    desc: '全年不打烊，365天在线，24小时营业，下楼即达',
    color: '#ec4899',
  },
  {
    icon: Smartphone,
    title: '智能设备',
    desc: '入场及智能设备均使用微信扫码/人脸识别，运动数据自动同步到手机',
    color: '#ec4899',
  },
  {
    icon: Trophy,
    title: '专业师资团队',
    desc: '世界冠军，国家一级运动员，国际健将，国家职业资格健美教练',
    color: '#ec4899',
  },
  {
    icon: Monitor,
    title: '线上管理系统',
    desc: '投资成本、运营成本低，全部线上操作管控，大大减少人力成本和管理成本',
    color: '#ec4899',
  },
  {
    icon: Users,
    title: '品牌力量',
    desc: '省体育局指导，接入省体育大数据平台，协助社区/物业洽谈免租，共同打造智慧体育',
    color: '#ec4899',
  },
]

const trainers = [
  {
    name: '杨胜超',
    role: '体操教练',
    certs: [
      '第十三届天津全运会体操团体冠军',
      '世界大学生运动会体操团体亚军',
      '第五届亚洲体操锦标赛体操团体冠军、个人吊环亚军',
      '第十七届韩国·仁川亚运会体操团体铜牌',
      '2014-2017年全国体操锦标赛团体连续4年夺冠',
      '个人全能冠军、单杠冠军',
      '美国·休斯顿国际体操邀请赛个人全能冠军、双杠冠军',
    ],
    img: 'https://gzzh.lafit.club/assets/images/first-trainer.jpg',
  },
  {
    name: '燕鹏斌',
    role: '健身教练',
    certs: [
      '国家职业资格教练认证',
      '国家一级社会指导员认证',
      '2017年贵州省大学生体能大赛总决赛亚军',
      'CPR资格认证',
      '国家二级运动员',
      '国家体育教师资格证',
      '贵州师范大学在读硕士',
    ],
    img: 'https://gzzh.lafit.club/assets/images/second-trainer.jpg',
  },
  {
    name: '李建媛',
    role: '瑜伽老师',
    certs: [
      '瑜伽教练导师资格证 TTC200',
      '九年资深瑜伽练习',
      '瑜伽教练导师资格证书',
    ],
    img: 'https://gzzh.lafit.club/assets/images/third-trainer.jpg',
  },
]

const scheduleData = [
  { name: '健身私教', times: { Thu: '10:00 - 11:30', Sat: '14:00 - 15:30' }, coach: '燕斌' },
  { name: '瑜伽白天', times: { Sun: '10:00 - 12:00' }, coach: '龙昕如' },
  { name: '瑜伽晚上', times: { Tue: '19:00 - 21:00' }, coach: '龙昕如' },
  { name: '成人爵士舞', times: { Wed: '19:30 - 21:30' }, coach: '蒋光悦' },
  { name: '少儿爵士舞', times: { Sun: '10:00 - 12:00' }, coach: '张颢迪' },
]

const DAYS = ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
const dayKeys: (keyof typeof scheduleData[0]['times'])[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export default function Gym() {
  const [activeDay, setActiveDay] = useState('Mon')

  return (
    <div className="min-h-screen pt-16">
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative h-[70vh] min-h-[500px] overflow-hidden">
        {/* Video background */}
        <div className="absolute inset-0">
          <video
            autoPlay muted loop playsInline
            className="w-full h-full object-cover"
          >
            <source src="https://gzzh.lafit.club/assets/images/zumba-0.8x-1080p.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.7) 100%)' }} />
        </div>

        <div className="relative z-10 h-full flex items-center justify-center text-center px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-pink-400 font-medium tracking-widest uppercase text-sm mb-4">运动是健康的源泉</p>
            <h1 className="text-5xl md:text-7xl font-black text-white mb-2 tracking-tight">
              家门口的健身中心
            </h1>
            <h2 className="text-4xl md:text-6xl font-black text-pink-400 mb-8 italic">
              百姓健身房
            </h2>
            <a
              href={FRANCHISE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white transition-all duration-300 hover:gap-3"
              style={{ background: '#ec4899', boxShadow: '0 0 30px rgba(236,72,153,0.5)' }}
            >
              加盟合作
              <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── Features ────────────────────────────────────────────────────── */}
      <section className="py-24 relative">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(236,72,153,0.05) 0%, transparent 70%)' }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }} viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-3">
              加盟 <span className="text-pink-400">合作</span>
            </h2>
            <img src="https://gzzh.lafit.club/assets/images/line-dec.png" alt="" className="mx-auto h-2 mt-2 mb-3" />
            <p className="text-gray-400">官方合作渠道</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.08 }} viewport={{ once: true }}
                className="flex gap-4 p-6 rounded-2xl"
                style={{
                  background: 'linear-gradient(135deg, rgba(236,72,153,0.08), rgba(236,72,153,0.02))',
                  border: '1px solid rgba(236,72,153,0.2)',
                }}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(236,72,153,0.15)' }}>
                  <f.icon className="w-6 h-6 text-pink-400" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white mb-1">{f.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Franchise CTA ────────────────────────────────────────────────── */}
      <section className="py-16 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(236,72,153,0.12), rgba(168,85,247,0.08))' }}>
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
              首期开放 <span className="text-pink-400">30家</span>，名额 <span className="text-pink-400">有限</span>！
            </h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              具备一定资金实力和风险意识，认同贵州智慧24小时百姓健身房品牌、产品及经营理念。
            </p>
            <a
              href={FRANCHISE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-10 py-4 rounded-xl font-bold text-white transition-all duration-300 hover:gap-3"
              style={{ background: '#ec4899', boxShadow: '0 0 30px rgba(236,72,153,0.5)' }}
            >
              立即申请加盟
              <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── Stores ───────────────────────────────────────────────────────── */}
      <section className="py-24 relative">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }} viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-3">
              现有 <span className="text-pink-400">门店</span>
            </h2>
            <img src="https://gzzh.lafit.club/assets/images/line-dec.png" alt="" className="mx-auto h-2 mt-2" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              { name: '小石城店', addr: '云岩区小石城磐城24栋1楼', img: 'https://gzzh.lafit.club/assets/images/store-01.jpg' },
              { name: '贵航店',   addr: '花溪区香江路255号',       img: 'https://gzzh.lafit.club/assets/images/store-02.jpg' },
            ].map((store, i) => (
              <motion.div
                key={store.name}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.15 }} viewport={{ once: true }}
                className="rounded-2xl overflow-hidden"
                style={{ border: '1px solid rgba(236,72,153,0.2)' }}
              >
                <img src={store.img} alt={store.name} className="w-full h-56 object-cover" />
                <div className="p-6" style={{ background: 'rgba(15,15,30,0.9)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-pink-400" />
                    <h3 className="text-lg font-bold text-white">{store.name}</h3>
                  </div>
                  <p className="text-gray-400 text-sm">{store.addr}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Schedule ─────────────────────────────────────────────────────── */}
      <section className="py-24 relative" style={{ background: 'rgba(0,0,0,0.2)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }} viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-3">
              课程 <span className="text-pink-400">时间表</span>
            </h2>
            <img src="https://gzzh.lafit.club/assets/images/line-dec.png" alt="" className="mx-auto h-2 mt-2 mb-3" />
            <p className="text-gray-500 text-sm">课表仅为示意，请进入微信公众号【贵州智慧健身】查看各店课表及报名</p>
          </motion.div>

          {/* Day filter tabs */}
          <div className="flex justify-center gap-2 mb-8 flex-wrap">
            {DAYS.map((day, i) => (
              <button
                key={day}
                onClick={() => setActiveDay(dayKeys[i])}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                style={{
                  background: activeDay === dayKeys[i] ? '#ec4899' : 'rgba(255,255,255,0.05)',
                  color: activeDay === dayKeys[i] ? 'white' : '#9ca3af',
                  border: '1px solid',
                  borderColor: activeDay === dayKeys[i] ? '#ec4899' : 'rgba(255,255,255,0.1)',
                }}
              >
                {day}
              </button>
            ))}
          </div>

          {/* Schedule rows */}
          <div className="space-y-3">
            {scheduleData.map((row) => {
              const times = Object.entries(row.times)
                .filter(([k]) => k === activeDay || !dayKeys.includes(k as any))
                .map(([, t]) => t)

              return (
                <div
                  key={row.name}
                  className="flex items-center gap-4 p-4 rounded-xl"
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <div className="w-32 flex-shrink-0">
                    <span className="text-white font-medium text-sm">{row.name}</span>
                  </div>
                  <div className="flex-1">
                    {times.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {times.map((t) => (
                          <span key={t} className="px-3 py-1 rounded-full text-xs font-medium"
                            style={{ background: 'rgba(236,72,153,0.15)', color: '#f472b6', border: '1px solid rgba(236,72,153,0.3)' }}>
                            {t}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-600 text-xs">—</span>
                    )}
                  </div>
                  <div className="w-24 text-right flex-shrink-0">
                    <span className="text-gray-400 text-xs">{row.coach}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── Trainers ──────────────────────────────────────────────────────── */}
      <section className="py-24 relative">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }} viewport={{ once: true }}
            className="text-center mb-4"
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-3">
              专业 <span className="text-pink-400">教练</span>
            </h2>
            <img src="https://gzzh.lafit.club/assets/images/line-dec.png" alt="" className="mx-auto h-2 mt-2 mb-3" />
            <p className="text-gray-400 mb-2">世界冠军，国家一级运动员，国际健将，国家职业资格健美教练。</p>
            <p className="text-gray-500 text-xs">
              <a href="https://www.v5kf.com/public/vdata/collect.html?sid=170569&fid=12316&first=1&ref=link&v5kf=mp.weixin.qq.com"
                target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:underline">
                热忱欢迎教练/老师加入团课/私教合作 →
              </a>
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            {trainers.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.12 }} viewport={{ once: true }}
                className="rounded-2xl overflow-hidden"
                style={{ border: '1px solid rgba(236,72,153,0.2)', background: 'rgba(10,10,25,0.9)' }}
              >
                <img src={t.img} alt={t.name} className="w-full h-64 object-cover object-top" />
                <div className="p-6">
                  <span className="text-xs text-pink-400 font-medium uppercase tracking-wider">{t.role}</span>
                  <h3 className="text-xl font-bold text-white mt-1 mb-3">{t.name}</h3>
                  <ul className="space-y-1">
                    {t.certs.map((c) => (
                      <li key={c} className="text-xs text-gray-400 leading-relaxed">• {c}</li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────────────────── */}
      <section className="py-16 text-center" style={{ background: 'rgba(236,72,153,0.08)' }}>
        <a
          href={FRANCHISE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-10 py-4 rounded-xl font-bold text-white transition-all duration-300 hover:gap-3"
          style={{ background: '#ec4899', boxShadow: '0 0 30px rgba(236,72,153,0.5)' }}
        >
          立即申请加盟
          <ArrowRight className="w-5 h-5" />
        </a>
      </section>
    </div>
  )
}
